import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AnpService } from '@shared/services/apis/motor-fiscal/anp.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { AnpCodigoStruct } from '@shared/services/structs/motor-fiscal/anp-codigo.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';

export interface AnpCodigoModalData {
  codigo: AnpCodigoStruct | null;
  regimes: LookupItemStruct[];
  unidades: LookupItemStruct[];
  indicadores: LookupItemStruct[];
}

@Component({
  selector: 'app-anp-codigo-modal',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatButtonModule, MatIconModule, MatSlideToggleModule, MatTooltipModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './anp-codigo-modal.component.html',
  styleUrls: ['./anp-codigo-modal.component.css'],
})
export class AnpCodigoModalComponent implements OnInit {
  public model: FormGroup;
  public _isLoading = false;
  public _isEdicao = false;
  public currencyMask = this.maskService.getCurrencyMask(4);
  // Na criação, a retenção acompanha a própria até o usuário digitar nela.
  private retencaoEditada = false;

  public readonly tooltipPbio =
    'Indica que este código ANP exige o preenchimento do percentual de mistura de biocombustível (pBio) na emissão da NF-e. O valor é informado na emissão, não no cadastro.';
  public readonly tooltipGlp =
    'Indica que este código ANP exige o preenchimento de pGLP, pGNn, pGNi e vPart na emissão da NF-e. Aplicável ao GLP. A soma de pGLP + pGNn + pGNi deve ser 100%.';

  constructor(
    private formBuilder: FormBuilder,
    private anpService: AnpService,
    private alertService: AlertService,
    private maskService: MaskService,
    public dialogRef: MatDialogRef<AnpCodigoModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AnpCodigoModalData
  ) {}

  ngOnInit(): void {
    const c = this.data.codigo;
    this._isEdicao = !!c;

    this.model = this.formBuilder.group({
      codigo: [c?.codigo ?? '', [Validators.required, Validators.pattern(/^\d{9}$/)]],
      descricao: [c?.descricao ?? '', [Validators.required, Validators.maxLength(255)]],
      idUnidadeMedida: [c?.idUnidadeMedida ?? null, Validators.required],
      idRegimeIcms: [c?.idRegimeIcms ?? null, Validators.required],
      idIndicadorImportacao: [c?.idIndicadorImportacao ?? null],
      aliquotaIcmsPorUnidade: [c?.aliquotaIcmsPorUnidade ?? 0],
      aliquotaIcmsRetencaoPorUnidade: [c?.aliquotaIcmsRetencaoPorUnidade ?? 0],
      exigePbio: [c?.exigePbio ?? false],
      exigeGrupoGlp: [c?.exigeGrupoGlp ?? false],
      isAtivo: [c?.isAtivo ?? true],
    });

    if (!this._isEdicao) {
      this.model.get('aliquotaIcmsPorUnidade')?.valueChanges.subscribe(valor => {
        if (!this.retencaoEditada)
          this.model.get('aliquotaIcmsRetencaoPorUnidade')?.setValue(valor ?? 0, { emitEvent: false });
      });
      // Só a edição do usuário emite aqui: a sincronização acima usa emitEvent: false.
      this.model.get('aliquotaIcmsRetencaoPorUnidade')?.valueChanges.subscribe(() => this.retencaoEditada = true);
    }
  }

  public somenteDigitos(event: any): void {
    const valor = (event.target.value || '').replace(/\D/g, '').slice(0, 9);
    this.model.get('codigo')?.setValue(valor);
  }

  public salvar(): void {
    if (this.model.invalid) {
      this.model.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios. O Código ANP deve conter exatamente 9 dígitos.', AlertType.warning);
      return;
    }

    this._isLoading = true;
    const request = this.model.getRawValue();
    const obs = this._isEdicao
      ? this.anpService.alterarCodigo(this.data.codigo!.idAnpEmpresa, request)
      : this.anpService.criarCodigo(request);

    obs.subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', this._isEdicao ? 'Código ANP atualizado.' : 'Código ANP adicionado.', AlertType.success);
        this.dialogRef.close({ saved: true });
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public cancelar(): void {
    this.dialogRef.close({ saved: false });
  }
}
